/**
 * resolveAssistantName — resolve the assistant / workspace display name.
 *
 * The owner's configured workspace name lives on `UserProfile.containerName`
 * (camelCase — built by `SessionProvider` from the API's snake_case
 * `container_name`). When it is absent we fall back to the product default,
 * "Xavior".
 *
 * Regression guard (PR #3266): display sites previously read
 * `profile.container_name` (snake_case), which does not exist on `UserProfile`,
 * so every read resolved to `undefined` and silently fell back to "Xavior".
 * Centralising the read against the typed `UserProfile` turns a snake_case drift
 * into a compile error (`TS2551`). Pinned by `assistantName.test.ts`.
 */
import type { UserProfile } from '@/types/session'

/** Product default shown when no workspace / assistant name is configured. */
export const DEFAULT_ASSISTANT_NAME = 'Xavior'

/**
 * Resolve the display name for the assistant from the session profile.
 *
 * Returns the configured `containerName` when present, otherwise
 * `DEFAULT_ASSISTANT_NAME`. Treats `null`, `undefined`, and `''` as "not
 * configured" — identical to the original `profile?.containerName || 'Xavior'`
 * expression it replaces.
 */
export function resolveAssistantName(
  profile: Pick<UserProfile, 'containerName'> | null | undefined,
): string {
  return profile?.containerName || DEFAULT_ASSISTANT_NAME
}
